// #3698 — splash stall watchdog decision logic.
//
// The boot splash ("Downloading existing data...") could stay up forever
// after a fresh sign-in: the boot await chain can wedge on a degraded
// connection while the background replication keeps emitting progress
// events. The old stuck detector (#3493) only looked at "no progress event
// for 25 seconds", so that class of stall was never flagged, never reported,
// and never retried.
//
// This module holds the pure decision logic so it can run under node for
// unit tests (same pattern as services/database-wrapper-passthrough.ts —
// app.component.ts itself cannot be loaded under node). app.component.ts
// delegates to evaluateSplashWatchdog() from its recursive splash-check
// timer; source-text tripwires in splash-watchdog.test.ts enforce that the
// delegation stays in place.

import { RunningClock } from '../services/running-clock';

/** How often the app component runs the watchdog check while the splash is up. */
export const SPLASH_STUCK_TICK_MS = 5_000;

/** No sync progress event for this long → show the stuck message + Retry button (#3493). */
export const SPLASH_STUCK_NO_PROGRESS_MS = 25_000;

/** No sync progress event for this long → emit the one-shot hang telemetry (#3493). */
export const SPLASH_HANG_TELEMETRY_MS = 30_000;

/**
 * Splash visible for this long in total → treat as stuck even if progress
 * events keep arriving (#3698). Sized well above any healthy first sync
 * (a normal login lands in well under a minute) but far below the
 * 13-minute stall that was reported.
 */
export const SPLASH_ABSOLUTE_STUCK_MS = 180_000;

/** Minimum spacing between automatic sync-engine kicks while stuck. */
export const SPLASH_AUTO_KICK_INTERVAL_MS = 60_000;

/**
 * O8 (#3986): once an auto-kick has fired and this much longer passes while still stuck, give
 * up on the sync-kick lever and escalate to ONE guarded full app reload (the sessionStorage
 * one-shot guard makes it un-loopable). ensureSyncsActive() is the wrong lever for most hang
 * classes (a wedged boot await, a stuck dialog, a dead auth-init) — a reload is the blunt
 * instrument that actually clears them.
 */
export const SPLASH_RELOAD_ESCALATION_MS = 60_000;

/**
 * #4401: how many boots may end in a watchdog reload before the app stops reloading and
 * tells the user instead.
 *
 * The one-shot guard above is per browser SESSION, so it stops a reload loop inside one
 * page but not the pattern a customer actually reported: reload, wedge again, reload
 * again, for over an hour. That is what "the app flashes white rapidly" is.
 *
 * Two, because the first reload genuinely does rescue some stalls (a fresh page gets a
 * fresh storage connection, which is the documented remedy for at least one browser
 * fault), while a third has never been observed to help anybody — by then the store is
 * not going to start answering on its own, and another blank screen is worse than an
 * honest message.
 */
export const WEDGED_BOOT_RELOAD_LIMIT = 2;

export interface SplashWatchdogState {
  /** Is the boot splash currently visible? */
  appLoading: boolean;
  /** Did the couch login already give up and defer to a retry? (precise stuck signal) */
  couchLoginRetryNeeded: boolean;
  /** Is the connection state currently Online? */
  connectionOnline: boolean;
  /** Milliseconds since the last DatabaseSyncProgressEvent. */
  noProgressMs: number;
  /** Milliseconds since the splash became visible. */
  splashUpMs: number;
  /** Milliseconds since the last automatic kick, or null if none happened yet this splash window. */
  msSinceLastAutoKick: number | null;
  /** One-shot guard: has the hang telemetry already been emitted this splash window? */
  hangEmitted: boolean;
  /**
   * Is the boot legitimately blocked on the user accepting the consent /
   * ToS dialog (BootStage.ColdLicenseDialog)? While true, no sync progress
   * flows because we are waiting on a human, so the time-based stall checks
   * must not fire (#3803).
   */
  awaitingUserConsent: boolean;
  /** #3991 (F24): the boot is deliberately held on the splash because the user is signed in, offline,
   *  and on a device that has never synced — we refuse to route them into empty onboarding and wait
   *  for connectivity instead. Like `awaitingUserConsent`, this is a boot that is BLOCKED, not one
   *  that is stalled: no sync progress can flow, so the time-based checks below would mis-fire and
   *  report a hang that is not happening. Optional so existing callers/tests are unaffected. */
  heldOfflineFirstLoad?: boolean;
  /**
   * O8 (#3986): milliseconds since the FIRST auto-kick fired this splash window, or null if no
   * auto-kick has fired yet. Because the first kick fires at stuck-detection, this measures how
   * long the sync-kick lever has been failing. Null while offline (no kick ever fires offline).
   */
  msSinceFirstAutoKick: number | null;
  /**
   * O8 (#3986): the sessionStorage one-shot guard — true once a reload-recovery has already been
   * attempted this browser session, so the escalation can never loop.
   */
  reloadAlreadyTried: boolean;
  /**
   * #4401: how many boots have already ended in a watchdog reload on this device, kept in
   * localStorage so it survives the reload itself. Past WEDGED_BOOT_RELOAD_LIMIT the
   * watchdog stops reloading and the user is told what is wrong instead. Optional so
   * existing callers and tests are unaffected; absent counts as zero.
   */
  wedgedBootCount?: number;
}

export interface SplashWatchdogDecision {
  /** Show the stuck message + Retry button. */
  stuck: boolean;
  /** Automatically restart the sync engine (dbProvider.ensureSyncsActive()). */
  autoKick: boolean;
  /** Emit the one-shot hang telemetry (Mixpanel event + Sentry silent problem). */
  emitHang: boolean;
  /**
   * O8 (#3986): perform ONE guarded full app reload (reloadAppAsync) and emit
   * boot_reload_recovery. The caller sets its sessionStorage guard so this fires at most once.
   */
  autoReload: boolean;
  /**
   * #4401: stop reloading and show the user an honest message about the device's storage.
   * Reached only when reloading has already been tried WEDGED_BOOT_RELOAD_LIMIT times and
   * the boot is stuck again — at which point another reload is just more flashing.
   */
  surfaceStorageFailure: boolean;
}

/** The elapsed times `evaluateSplashWatchdog` is given, as `SplashWatchdogTimes` measures them. */
export interface SplashWatchdogElapsed {
  noProgressMs: number;
  splashUpMs: number;
  msSinceLastAutoKick: number | null;
  msSinceFirstAutoKick: number | null;
  /** Time since the splash was shown that the page was frozen, and that no other field counts (#6093). */
  notRunningMs: number;
}

/**
 * The most one gap between two watchdog reads may count (#6093): two ticks' worth. The tick
 * reads the clock every SPLASH_STUCK_TICK_MS while the splash is up, so a gap longer than this
 * means the page was frozen, not that the boot was slow.
 */
export const SPLASH_MAX_TICK_CREDIT_MS = 2 * SPLASH_STUCK_TICK_MS;

/**
 * The moments the splash watchdog measures from: when the splash was shown, when sync last
 * made progress, and when the watchdog last and first kicked the sync engine.
 *
 * Kept here rather than as fields on the app component so the way elapsed time is measured
 * can be tested under node with a clock driven by hand.
 *
 * #6093: every elapsed time is RUNNING time. When a phone backgrounds the app, its JavaScript
 * stops and the tick stops with it; on waking, the first tick used to see the whole freeze as
 * "no progress" and reported a stuck boot — in production 27 of 112 SplashStuckOnBoot events,
 * one of them in the same second the boot finished. The same wrong time also drove the
 * auto-kick and the reload escalation. A gap between reads longer than
 * SPLASH_MAX_TICK_CREDIT_MS now counts as that much and no more; the rest is reported as
 * `notRunningMs` so a reader can still see a freeze happened.
 */
export class SplashWatchdogTimes {
  private readonly clock: RunningClock;
  private splashShownAtMs: number;
  private notRunningAtArmMs: number;
  private lastProgressAtMs: number;
  private lastAutoKickAtMs: number | null = null;
  private firstAutoKickAtMs: number | null = null;

  // `monotonicNowMs` is `performance.now()` in production
  constructor(monotonicNowMs: () => number) {
    this.clock = new RunningClock(monotonicNowMs, SPLASH_MAX_TICK_CREDIT_MS);
    const now = this.nowMs();
    this.splashShownAtMs = now;
    this.notRunningAtArmMs = this.clock.notRunningMs();
    this.lastProgressAtMs = now;
  }

  private nowMs(): number {
    return this.clock.read();
  }

  // a new splash window: everything is measured from now, and no kick has happened yet
  arm(): void {
    const now = this.nowMs();
    this.splashShownAtMs = now;
    this.notRunningAtArmMs = this.clock.notRunningMs();
    this.lastProgressAtMs = now;
    this.lastAutoKickAtMs = null;
    this.firstAutoKickAtMs = null;
  }

  // sync made progress just now
  recordProgress(): void {
    this.lastProgressAtMs = this.nowMs();
  }

  // O8 (#3986): remember when the FIRST kick fired too, so the watchdog can measure how long
  // the sync-kick lever has been failing before escalating to a reload
  recordAutoKick(): void {
    const now = this.nowMs();
    this.lastAutoKickAtMs = now;
    if (this.firstAutoKickAtMs === null) {
      this.firstAutoKickAtMs = now;
    }
  }

  // leaving the consent gate: time spent reading the dialog is the user's, not the boot's,
  // so a stall after it starts from zero (see the consent-exit note in boot-sequence.md)
  resetAfterConsent(): void {
    this.lastProgressAtMs = this.nowMs();
    this.lastAutoKickAtMs = null;
    this.firstAutoKickAtMs = null;
  }

  // every elapsed time, in running time. Each call also advances the running clock, which is
  // why the app's 5 s check calling it is what keeps a running page's time fully counted
  elapsed(): SplashWatchdogElapsed {
    const now = this.nowMs();
    return {
      noProgressMs: now - this.lastProgressAtMs,
      splashUpMs: now - this.splashShownAtMs,
      msSinceLastAutoKick: this.lastAutoKickAtMs === null ? null : now - this.lastAutoKickAtMs,
      msSinceFirstAutoKick: this.firstAutoKickAtMs === null ? null : now - this.firstAutoKickAtMs,
      notRunningMs: this.clock.notRunningMs() - this.notRunningAtArmMs,
    };
  }
}

export function evaluateSplashWatchdog(state: SplashWatchdogState): SplashWatchdogDecision {
  // if the splash is not visible, there is nothing to watch
  if (!state.appLoading) {
    return { stuck: false, autoKick: false, emitHang: false, autoReload: false, surfaceStorageFailure: false };
  }

  // #3803: the boot is legitimately blocked on the user accepting the
  // consent / ToS dialog (BootStage.ColdLicenseDialog). No sync progress
  // flows while we wait on a human, so the time-based stall checks below
  // would mis-fire SplashStuckOnBoot, show the "stuck + Retry" message, and
  // kick sync for no reason. Resume watching as soon as the dialog closes
  // (licenseDialogSubject -> false), which still catches any real
  // post-consent stall.
  if (state.awaitingUserConsent) {
    return { stuck: false, autoKick: false, emitHang: false, autoReload: false, surfaceStorageFailure: false };
  }

  // #3991 (F24): same shape as the consent gate above — the boot is deliberately HELD, not stuck.
  // The user is signed in, offline, and on a device that has never synced, so we hold the splash with
  // an honest message rather than route them into empty onboarding. No sync progress can flow while
  // we wait for connectivity, so without this the watchdog would report a hang that is not happening,
  // and stack its "stuck + Retry" message on top of the honest one. Coming back online reloads the
  // app, which starts a fresh boot the watchdog does watch.
  if (state.heldOfflineFirstLoad) {
    return { stuck: false, autoKick: false, emitHang: false, autoReload: false, surfaceStorageFailure: false };
  }

  // #5449 removed a third suppression that used to sit here, for the whole
  // `BootStage.SqliteMigration` stage. It existed because the copy ran on this screen
  // and took about five minutes on a real account, so every lever below fired during
  // it — including a full page reload at about 85 s, which killed a copy that was
  // working. The copy now happens in the background while the app is usable, and what
  // remains on this screen is finishing a delta of tens of documents. That is ordinary
  // boot work of ordinary length, so the watchdog watches it like everything else —
  // and a device that really does wedge there is once again something the watchdog can
  // report, which the suppression had made impossible.
  const noProgressStuck = state.noProgressMs > SPLASH_STUCK_NO_PROGRESS_MS;
  // the absolute threshold catches the #3698 case: boot wedged while the
  // background replication keeps resetting the no-progress timer
  const absoluteStuck = state.splashUpMs > SPLASH_ABSOLUTE_STUCK_MS;
  const stuck = state.couchLoginRetryNeeded || noProgressStuck || absoluteStuck;

  const emitHang = !state.hangEmitted
    && (state.noProgressMs > SPLASH_HANG_TELEMETRY_MS || absoluteStuck);

  // kick only while online (there is nothing to restart against offline),
  // immediately on first stuck detection, then rate-limited
  const kickDue = state.msSinceLastAutoKick == null
    || state.msSinceLastAutoKick >= SPLASH_AUTO_KICK_INTERVAL_MS;
  const autoKick = stuck && state.connectionOnline && kickDue;

  // O8 (#3986): escalate to one guarded reload when the sync-kick lever has been failing.
  // Two DELIBERATE behaviors (chosen, not accidental):
  //   (a) The timing is path-dependent. A no-progress hang trips `stuck` at ~25 s, so the first
  //       auto-kick fires then and this reload lands ~60 s later (~85-90 s). A boot that keeps
  //       emitting progress while wedged only trips `stuck` at the 180 s absolute bound, so its
  //       first kick — and therefore this reload — land near ~240 s. Both are acceptable.
  //   (b) An OFFLINE-stuck boot never auto-reloads: autoKick requires connectionOnline, so
  //       msSinceFirstAutoKick stays null offline; and we also gate autoReload on
  //       connectionOnline here. Correct — a reload cannot fix offline; the stuck UI + Retry is
  //       the offline affordance.
  // The sessionStorage one-shot guard (reloadAlreadyTried) makes this un-loopable.
  // The boot has been stuck long enough that the sync-kick lever has clearly failed. From
  // here one of two things happens: another reload, or an honest message. Both hang off
  // this same moment, which is why it is worked out once.
  const escalationDue = stuck
    && state.connectionOnline
    && state.msSinceFirstAutoKick != null
    && state.msSinceFirstAutoKick >= SPLASH_RELOAD_ESCALATION_MS;

  // #4401: is another reload going to happen at all? Two separate things can stop one, and
  // BOTH have to be checked here — missing either is what would leave the user on a stuck
  // splash with no reload coming and nothing said about it:
  //
  //  - the sessionStorage one-shot guard, which stops a SECOND reload inside this browser
  //    session. It survives `location.reload()` in the same tab (the reload path says so
  //    itself), so on the boot right after a reload — the very boot that proves reloading
  //    did not help — this is already true.
  //  - the wedged-boot budget, which counts reloads across sessions and stops the pattern
  //    the customer reported: wedge, reload, relaunch, wedge, reload, for over an hour.
  const budgetSpent = (state.wedgedBootCount ?? 0) >= WEDGED_BOOT_RELOAD_LIMIT;
  const reloadStillAllowed = !state.reloadAlreadyTried && !budgetSpent;

  const autoReload = escalationDue && reloadStillAllowed;
  // No reload is coming, so stop and say what is actually wrong. Note this is decided at
  // the moment a reload WOULD have been due, never merely whenever the count is high: a
  // device that was rescued and is booting normally carries a stale count and must not be
  // sent to an error screen for it.
  const surfaceStorageFailure = escalationDue && !reloadStillAllowed;

  return { stuck, autoKick, emitHang, autoReload, surfaceStorageFailure };
}
